/**
 * 감소율 막대 글 — 「████████████████░░░░ 82.4% 감소」(20칸). 백엔드 sqlTuning/report.js `reductionBar` 와 **같은 모양**이다
 * (리포트 .md 는 서버가, 튜닝 사례 상세는 화면이 만든다). 화면의 ReportView 가 이 모양을 알아보고 색 막대로 그린다.
 *
 * 낱말은 리포트 본문(데이터)의 일부라 한국어로 고정한다 — 서버 .md 와 글자 그대로 같아야 한다(CLAUDE.md i18n 「번역하지 않는 것」 표).
 * 화면에 보일 때는 ReportView 가 `common:stn.bar.*` 로 번역한다.
 */
export const BAR_CELLS = 20;
export const BAR_WORD = { down: '감소', up: '증가', same: '변화 없음' };
export const BAR_RE = /^[█░]{20} (\d+(?:\.\d+)?)% (감소|증가|변화 없음)$/;

/** (전 − 후) / 전 × 100, 소수 한 자리. 전이 없거나 0 이하면 null */
export function reductionPct(before, after) {
  if (before == null || after == null) return null;
  const b = Number(before);
  const a = Number(after);
  if (!Number.isFinite(b) || !Number.isFinite(a) || b <= 0) return null;
  const pct = Math.round(((b - a) / b) * 1000) / 10;
  // 남은 것이 있는데 반올림으로 100% 가 되면 「전부 없어졌다」 로 읽힌다 — 99.9 에서 멈춘다
  return pct >= 100 && a > 0 ? 99.9 : pct;
}

export function barText(pct) {
  if (pct == null) return null;
  const n = Math.round((Math.min(Math.abs(pct), 100) / 100) * BAR_CELLS);
  const word = pct > 0 ? BAR_WORD.down : pct < 0 ? BAR_WORD.up : BAR_WORD.same;
  return `${'█'.repeat(n)}${'░'.repeat(BAR_CELLS - n)} ${Math.abs(pct)}% ${word}`;
}

/** 초 — 단위 없이 숫자만(소수 셋째 자리까지) */
export const secNumber = (v) => (Math.round(Number(v) * 1000) / 1000).toLocaleString('en-US', { maximumFractionDigits: 3 });

/**
 * 지표의 단위 — 'sec' | 'block' | 'row' | null. 시간이면 초, 이름에 읽기·Buffers·Reads·블록이 있으면 블록
 * (논리·물리 읽기는 둘 다 **블록 수**다 — 물리 읽기도 읽은 횟수가 아니다), 행·건수면 행. 모르면 적지 않는다.
 */
export function unitOf(label, isTime) {
  if (isTime) return 'sec';
  const s = String(label || '');
  // 판별용 낱말(읽기·블록 / 행·건수) — 사례의 개선 효과 글(데이터)에서 찾는 것이라 번역 대상이 아니다. 위 NUM 처럼 유니코드로 적는다
  if (new RegExp('\uC77D\uAE30|\uBE14\uB85D|buffers?|reads?|gets', 'i').test(s)) return 'block';
  if (new RegExp('\uD589|\uAC74\uC218|rows?', 'i').test(s)) return 'row';
  return null;
}

/**
 * 감소량 글 — 전 − 후(백엔드 report.js `reductionAmount` 와 같은 모양). 줄었으면 「−1,573,682」, 늘었으면 「+120」.
 * 시간은 초(단위 없이 숫자만 — 단위는 지표 이름 옆 괄호에 적는다). 원문이 K·M 처럼 줄여 적은 값이면 정확한 차이가 아니므로 `approx` 로 「약」 을 붙인다.
 */
export function amountText(before, after, { isTime = false, approx = false } = {}) {
  if (before == null || after == null) return null;
  const d = Number(before) - Number(after);
  if (!Number.isFinite(d)) return null;
  if (d === 0) return '0';
  const abs = Math.abs(d);
  const body = isTime
    ? secNumber(abs)
    : (approx && abs >= 1e6 ? `${Math.round(abs / 1e5) / 10}M` : approx && abs >= 1e3 ? `${Math.round(abs / 100) / 10}K` : Math.round(abs * 1000) / 1000).toLocaleString('en-US');
  return `${approx ? '≈ ' : ''}${d > 0 ? '−' : '+'}${body}`;
}

/** 막대 글 → { pct, kind: 'down'|'up'|'same' } | null */
export function parseBar(text) {
  const m = BAR_RE.exec(String(text || '').trim());
  if (!m) return null;
  const kind = m[2] === BAR_WORD.up ? 'up' : m[2] === BAR_WORD.same ? 'same' : 'down';
  return { pct: Number(m[1]), kind };
}

const SCALE = { K: 1e3, M: 1e6, G: 1e9 };
const TIME = { ms: 0.001, s: 1, '초': 1, '분': 60 };   // 초·분
// 천 단위 쉼표는 세 자리씩일 때만 숫자다 — 「196, 수행 시간」 의 쉼표를 숫자로 먹지 않게
const NUM = '(\\d+(?:,\\d{3})*(?:\\.\\d+)?)\\s*([KMG])?\\s*(ms|s|\\uCD08|\\uBD84)?';
const PAIR_RE = new RegExp(`${NUM}\\s*(?:→|->|=>)\\s*${NUM}`, 'g');

const toValue = (num, scale, unit) => {
  let v = Number(String(num).replace(/,/g, ''));
  if (!Number.isFinite(v)) return null;
  if (scale) v *= SCALE[scale];
  if (unit) v *= TIME[unit] || 1;
  return v;
};

/**
 * 「개선 효과」 글에서 「이름 전 → 후」 짝을 뽑는다 — 「논리 읽기 6,908 → 6,722, 0.36초 → 0.32초」.
 * 이름은 짝 앞의 글(구분자 , · : ( 뒤부터). 시간 단위는 초로 맞춰 견준다(ms ↔ 초 섞여도 된다).
 * @returns [{ label, before, after, beforeText, afterText, isTime }]
 */
export function parseImprovement(text) {
  const s = String(text || '');
  const out = [];
  let prevEnd = 0;
  PAIR_RE.lastIndex = 0;
  let m;
  while ((m = PAIR_RE.exec(s)) !== null) {
    const lead = s.slice(prevEnd, m.index);
    const cut = Math.max(lead.lastIndexOf(','), lead.lastIndexOf('·'), lead.lastIndexOf(':'), lead.lastIndexOf('('), lead.lastIndexOf('\n'));
    const label = lead.slice(cut + 1).replace(/^[\s,]+|[\s:]+$/g, '').trim();
    const unitA = m[3] || m[6];
    const unitB = m[6] || m[3];
    const before = toValue(m[1], m[2], unitA);
    const after = toValue(m[4], m[5], unitB);
    if (before != null && after != null) {
      out.push({
        label, before, after,
        beforeText: `${m[1]}${m[2] || ''}${m[3] || ''}`, afterText: `${m[4]}${m[5] || ''}${m[6] || ''}`,
        isTime: !!(unitA || unitB),
      });
    }
    prevEnd = m.index + m[0].length;
  }
  return out;
}
